import { useState } from "react";

import { faRefresh } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "src/base-components/Button";
import { Card } from "src/base-components/Card";
import { Checkbox } from "src/base-components/Checkbox";
import { SubHeader } from "src/flow/SubHeader";
import { SessionsTable } from "src/powertools/SessionsTable";
import { useOrganizationSessions } from "src/powertools/useOrganizationSessions";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

const PAGE_SIZE = 50;

export const SessionsPage: React.FC = () => {
  const { orgId } = useParamsDecode<DashboardPageParams>();
  const [skip, setSkip] = useState(0);
  const [includeExpired, setIncludeExpired] = useState(false);

  const sessionsQuery = useOrganizationSessions(orgId, {
    skip,
    limit: PAGE_SIZE,
    includeExpired,
  });

  const handleToggleIncludeExpired = () => {
    setIncludeExpired((value) => !value);
    setSkip(0);
  };

  const total = sessionsQuery.data?.total ?? 0;
  const hasMore = sessionsQuery.data?.has_more ?? false;
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showPagination =
    !sessionsQuery.isLoading && !sessionsQuery.isError && total > 0;

  return (
    <div className="h-full bg-gray-100">
      <SubHeader title="Sessions">
        <label className="flex h-full cursor-pointer items-center gap-x-2 border-gray-200 border-l px-3.5 font-inter-normal-12px text-gray-700">
          <Checkbox
            checked={includeExpired}
            dataLoc="sessions-include-expired"
            onChange={handleToggleIncludeExpired}
          />
          Include expired sessions
        </label>
        <SubHeader.Button
          dataLoc="sessions-refresh-button"
          disabled={sessionsQuery.isFetching}
          icon={faRefresh}
          spin={sessionsQuery.isFetching}
          tooltip="Refresh sessions"
          onClick={() => sessionsQuery.refetch()}
        />
      </SubHeader>

      <div className="h-[calc(100%-3rem)] p-5">
        <Card
          className="flex h-full flex-col pt-1"
          variant="inverted"
          fullWidth
        >
          <Card.Content className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1">
              <SessionsTable
                data={sessionsQuery.data?.users ?? []}
                includeExpired={includeExpired}
                isError={sessionsQuery.isError}
                isLoading={sessionsQuery.isLoading}
              />
            </div>
            {showPagination && (
              <div className="mt-3 flex items-center justify-between border-gray-100 border-t pt-3 font-inter-normal-12px text-gray-500">
                <span data-loc="sessions-total">
                  {total} session{total !== 1 ? "s" : ""}
                </span>
                <div className="flex items-center gap-x-3">
                  <Button
                    dataLoc="sessions-prev-page"
                    disabled={skip === 0}
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setSkip((current) => Math.max(0, current - PAGE_SIZE))
                    }
                  >
                    Previous
                  </Button>
                  <span className="tabular-nums">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    dataLoc="sessions-next-page"
                    disabled={!hasMore}
                    size="sm"
                    variant="secondary"
                    onClick={() => setSkip((current) => current + PAGE_SIZE)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card.Content>
        </Card>
      </div>
    </div>
  );
};
